"use client";

// ArtifactMobile — the phone artifact experience (Figma 1314-2589 / 1306-3224).
//
// Opening the artifacts view does NOT jump straight into full reading. At the top
// you still see the project navigator (passed in as `topNav`) AND the artifact-title
// ticker + progress bar, with the live organism behind. Only as you SCROLL does the
// navigator scroll away (the ticker stays, sticky), and a near-white reading ramp
// fades in behind the text — "ready for serious reading". Scroll back to the top and
// the whole navigator returns. Swiping left/right moves between documents.
//
// One vertical scroll owns everything: [topNav] · [sticky ticker + bar] · [doc]. Only
// the ACTIVE document renders (keyed), always via the full renderer — so nothing
// resizes/jumps when you move between documents.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { type OsNode } from "../../../lib/os/tree";
import ArtifactTabNav from "./ArtifactTabNav";
import ArtifactDoc from "./ArtifactDoc";
import IconCircle from "../../IconCircle";
import { tension } from "./swipeTension";
import { canScrollX } from "../../ether/etherNav";
import { writeReadRamp } from "../../../lib/os/readRamp";
import { useBandHeight } from "../../../lib/os/useBandHeight";
import { useLockFold } from "../../../lib/os/lockFold";
import { keepResumable } from "../../../lib/os/resume";
import { restoreScroll } from "../../../lib/stage/memory";

const RAMP_PX = 200; // scroll distance over which the nav clears + the scrim ramps in

// The ramp is written in three channels, quantised by what each one costs —
// opacity fine, blur radii and the navigator's fold coarse (lib/os/readRamp).
// Quantising only the cheap channel wasn't enough: the two expensive ones (a new
// gaussian radius, a new max-height) were still being asked for ~100 times
// across the ramp, which is exactly the hiccup you feel scrolling back UP.
function setRamp(v: number) {
  // Resting the organism is writeReadRamp's job now, for EVERY reading surface
  // rather than this one.
  //
  // It used to be published here as the live ramp — which meant that at ramp
  // 0.3 the bloom was only 30% rested and still redrawing every frame behind a
  // frost that was already fully lit. A backdrop-filter re-runs whenever its
  // backdrop repaints, so a partly-rested organism kept paying for the blur on
  // every single frame. That is why the reader still stuttered after the frost
  // itself was made cheap. Reading mode is binary now, and so is the rest.
  writeReadRamp(v, "art");
}

export default function ArtifactMobile({
  pages,
  index,
  onIndex,
  highlight,
  topNav,
  editing = false,
  onToggleEdit,
  onSaveDocument,
  onSaveNow,
  onCancelEdit,
  edits,
  readKey = null,
  resumeTop = null,
}: {
  /** where how far the open document was read is kept (lib/os/places reading), when it is kept */
  readKey?: string | null;
  /** how far down the open document opens, when the person is continuing where they were */
  resumeTop?: number | null;
  pages: OsNode[];
  index: number;
  onIndex: (i: number) => void;
  highlight?: string | null;
  topNav?: ReactNode;
  /** notion-like inline editing on the focused document. */
  editing?: boolean;
  /** flip the edit ↔ done toggle (undefined hides the affordance). */
  onToggleEdit?: () => void;
  /** serialized markdown of the focused doc on each edit (upstream debounces).
   *  `name` is the raw DB identifier a dimension write needs (its real name). */
  onSaveDocument?: (key: string, projectId: string | undefined, markdown: string, name?: string) => void;
  /** explicit save — flushes the buffered edit; resolves true only when the
   *  write landed (the shell then exits edit mode + flashes "memory updated"). */
  onSaveNow?: (key: string) => Promise<boolean>;
  /** cancel — discards the edit (restoring the snapshot if an autosave already
   *  landed) and leaves edit mode; the shell says "changes discarded". */
  onCancelEdit?: (key: string) => Promise<void>;
  /** locally-edited bodies by node key — so switching docs + back keeps the edit. */
  edits?: Record<string, string>;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // the pinned band (declared up here, before the early return below, with the
  // detached fold that drives it from THIS reader's own scroller — lib/os/lockFold)
  const stickyRef = useRef<HTMLDivElement>(null);
  useLockFold(stickyRef, { scroller: scrollRef });
  const n = pages.length;
  const activeIdx = Math.max(0, Math.min(index, n - 1));

  // a new document opens from the TOP (navigator back in view, scrim cleared), unless the person is
  // continuing where they were: then it opens where they left it (a plain open from a link stays at the top)
  const resumed = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    setRamp(0);
    if (!el) return;
    if (!resumed.current && resumeTop && resumeTop > 0) {
      resumed.current = true;
      return restoreScroll(el, resumeTop);
    }
    el.scrollTo({ top: 0 });
  }, [activeIdx, resumeTop]);
  // how far it was read, kept a beat after the scroll settles (the stage's own rhythm, lib/stage/memory)
  const keepTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (keepTimer.current) clearTimeout(keepTimer.current); }, []);
  useEffect(() => () => setRamp(0), []);

  // THE SEAM (globals.css): the document dissolves under the head in its own
  // frame. It needs two numbers on the document: where the scroller's top edge
  // falls inside it (per scroll frame) and the head's height (per layout).
  // Both are registered, non-inheriting properties, so a write restyles this
  // one element and none of the document under it.
  const docRef = useRef<HTMLDivElement>(null);
  const writeSeam = () => {
    const sc = scrollRef.current;
    const doc = docRef.current;
    if (!sc || !doc) return;
    doc.style.setProperty("--seam-y", `${Math.round(sc.scrollTop - doc.offsetTop)}px`);
  };
  useEffect(() => {
    const head = stickyRef.current;
    const doc = docRef.current;
    if (!head || !doc) return;
    const measure = () => {
      doc.style.setProperty("--seam-open", `${head.offsetHeight}px`);
      writeSeam();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(head);
    ro.observe(doc);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  // one measure + one write per FRAME, never per scroll event (a trackpad fires
  // several per frame, and each one restyled the chrome).
  const scrollFrame = useRef(0);
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (scrollFrame.current) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = 0;
      const range = el.scrollHeight - el.clientHeight;
      window.dispatchEvent(new CustomEvent("innernet:art-read", { detail: range > 0 ? el.scrollTop / range : 0 }));
      setRamp(el.scrollTop / RAMP_PX);
      writeSeam();
    });
    if (readKey) {
      if (keepTimer.current) clearTimeout(keepTimer.current);
      const top = el.scrollTop;
      keepTimer.current = setTimeout(() => keepResumable(readKey, Math.round(top)), 160);
    }
  };
  useEffect(() => () => { if (scrollFrame.current) cancelAnimationFrame(scrollFrame.current); }, []);

  // the pinned band's bottom edge, in viewport px — the veil's ground tint is a
  // full-viewport layer masked back to it (law_one_ground_gradient).
  useBandHeight(stickyRef, true, "--art-band-h", 6);

  // The navigator FOLDS as you read — it does not scroll away. Folding needs its
  // natural height as a number, so the collapse can run off --art-ramp in CSS
  // with no per-frame JS. Measured once and on resize.
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const write = () => {
      const h = el.scrollHeight;
      if (h > 0) {
        document.documentElement.style.setProperty("--art-nav-h", `${h}px`);
        stickyRef.current?.style.setProperty("--lock-h", `${h}px`);
      }
    };
    write();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(write) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); document.documentElement.style.removeProperty("--art-nav-h"); };
  }, [topNav]);

  if (n === 0) return null;

  return (
    <div className="art-mobile">
      <div ref={scrollRef} className="art-mobile-scroll os-art-scroll" data-art-scroll data-noswipe onScroll={onScroll}>
        {/* PINNED chrome — the navigator (crumb · ticker · view chips) and the
            document tabs hold their place together, and the document folds away
            underneath them. They used to be split: the navigator scrolled off as
            reading content while only the tabs stayed, so the view chips left
            the screen on a page whose whole point is that its chrome doesn't
            move. One sticky region now, one veil, one behaviour. */}
        <div ref={stickyRef} className="art-mobile-sticky os-lock--detached">
          {topNav && <div ref={navRef} className="art-mobile-topnav">{topNav}</div>}
          {/* the veil: progressive frost (strongest at the top, easing down so
              nothing under it clashes) over THE ground gradient, masked back to
              this band — see law_one_ground_gradient. */}
          <div className="art-mobile-veil" aria-hidden><i /><i /></div>
          <ReaderTabsRow
            pages={pages}
            index={activeIdx}
            onIndex={onIndex}
            editing={editing}
            onToggleEdit={onToggleEdit}
            onSaveNow={onSaveNow}
            onCancelEdit={onCancelEdit}
          />
          {/* the long full-width scrubber is retired here too — reading
              progress rides under the document's own title (ReadingBar). */}
        </div>
        <ReaderDoc
          key={activeIdx}
          docRef={docRef}
          pages={pages}
          index={activeIdx}
          onIndex={onIndex}
          highlight={highlight}
          editing={editing}
          onToggleEdit={onToggleEdit}
          onSaveDocument={onSaveDocument}
          edits={edits}
          widthOf={() => scrollRef.current?.clientWidth || 360}
          seamTransition
        />
      </div>
    </div>
  );
}

/** can this page be edited in place? An artifact (`art:<uuid>`) or a dimension
 *  (`dim:<slug>`, keyed by projectId + its raw name). Raw captures, node cards
 *  and parked ideas are read-only, so the edit affordance stays hidden for them. */
function editableKey(page: OsNode | undefined, canToggle: boolean): boolean {
  if (!page || !canToggle) return false;
  return page.key.startsWith("art:") || (page.key.startsWith("dim:") && !!page.projectId);
}

/**
 * The reader's tab row: the document tabs, and the edit affordance (or, while
 * editing, "editing memory" + cancel + save). It is the navigator's last row in
 * the reader — on the stage it sits in the stage's head, under the folding
 * navigator; in the old reader it sits in the pinned band.
 */
export function ReaderTabsRow({
  pages,
  index,
  onIndex,
  editing = false,
  onToggleEdit,
  onSaveNow,
  onCancelEdit,
}: {
  pages: OsNode[];
  index: number;
  onIndex: (i: number) => void;
  editing?: boolean;
  onToggleEdit?: () => void;
  onSaveNow?: (key: string) => Promise<boolean>;
  onCancelEdit?: (key: string) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const n = pages.length;
  const activeIdx = Math.max(0, Math.min(index, n - 1));
  const page = pages[activeIdx];
  const canEdit = editableKey(page, !!onToggleEdit);
  const isEditing = editing && canEdit;

  // the explicit save: flush the buffered edit; the shell exits edit mode +
  // flashes "memory updated" only when the write truly landed.
  const saveNow = async () => {
    if (saving || !page) return;
    if (!onSaveNow) {
      onToggleEdit?.();
      return;
    }
    setSaving(true);
    try {
      await onSaveNow(page.key);
    } finally {
      setSaving(false);
    }
  };

  const cancelNow = async () => {
    if (saving || cancelling || !page) return;
    if (!onCancelEdit) {
      onToggleEdit?.();
      return;
    }
    setCancelling(true);
    try {
      await onCancelEdit(page.key);
    } finally {
      setCancelling(false);
    }
  };

  if (!n) return null;
  return (
    <div className="art-mobile-stickyrow">
      <ArtifactTabNav pages={pages} index={activeIdx} onIndex={onIndex} />
      {canEdit && isEditing && (
        // in edit mode the row states it plainly: a breathing mark +
        // "editing memory", the quiet way out (cancel), and the save that
        // commits the update. Escape does what cancel does.
        <span className="art-edit-live" data-noswipe>
          <span className="art-edit-flag" aria-live="polite"><i aria-hidden />editing memory</span>
          <button
            type="button"
            className="quiet-link art-edit-cancel"
            onClick={cancelNow}
            disabled={saving || cancelling}
            title="discard this edit (esc)"
          >
            {cancelling ? "discarding…" : "cancel"}
          </button>
          <button
            type="button"
            className="chip art-edit-save"
            onClick={saveNow}
            disabled={saving || cancelling}
            title="save this memory"
          >
            {saving ? "saving…" : "save"}
          </button>
        </span>
      )}
      {canEdit && !isEditing && (
        <IconCircle
          size="sm"
          data-noswipe
          onClick={onToggleEdit}
          title="edit this document (or double-click the text)"
          aria-label="edit this document"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
        </IconCircle>
      )}
    </div>
  );
}

/**
 * The document itself: the full renderer, double-click to edit, and a touch
 * swipe sideways to the next or previous document (vertical is the page's own
 * scroll). Mount it keyed by the document, so each one opens fresh.
 */
export function ReaderDoc({
  pages,
  index,
  onIndex,
  highlight,
  editing = false,
  onToggleEdit,
  onSaveDocument,
  edits,
  docRef,
  widthOf,
  seamTransition = false,
}: {
  pages: OsNode[];
  index: number;
  onIndex: (i: number) => void;
  highlight?: string | null;
  editing?: boolean;
  onToggleEdit?: () => void;
  onSaveDocument?: (key: string, projectId: string | undefined, markdown: string, name?: string) => void;
  edits?: Record<string, string>;
  docRef?: React.Ref<HTMLDivElement>;
  /** the width a swipe is measured against (the reader's own column). */
  widthOf?: () => number;
  /** the old reader's seam rides the head's spring on this element. */
  seamTransition?: boolean;
}) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x0: number; y0: number; lastX: number; lastT: number; vx: number; axis: "" | "x" | "y"; pid: number; target: HTMLElement | null } | null>(null);
  const n = pages.length;
  const activeIdx = Math.max(0, Math.min(index, n - 1));
  const wrap = (i: number) => ((i % n) + n) % n;
  const page = pages[activeIdx];
  const canEdit = editableKey(page, !!onToggleEdit);
  const isEditing = editing && canEdit;
  const W = () => widthOf?.() || 360;

  // if you switch to a NON-editable doc (a raw capture / node card — neither an
  // artifact nor a dimension) while edit mode is on, drop out of editing —
  // otherwise its toggle is hidden and the swipe stays locked, with no way back.
  const activeKey = page?.key ?? "";
  useEffect(() => {
    if (editing && !activeKey.startsWith("art:") && !activeKey.startsWith("dim:")) onToggleEdit?.();
  }, [editing, activeKey, onToggleEdit]);

  // horizontal swipe on the DOCUMENT switches document; vertical is native scroll.
  // TOUCH ONLY — a mouse drag on desktop belongs to native text selection (this
  // reader is now used on every width); while EDITING the pointer is the caret.
  // Desktop switches docs via the visible ticker (ArtifactTabNav) + arrow keys.
  const onDown = (e: React.PointerEvent) => {
    if (editing || e.pointerType !== "touch") return;
    drag.current = { x0: e.clientX, y0: e.clientY, lastX: e.clientX, lastT: e.timeStamp, vx: 0, axis: "", pid: e.pointerId, target: e.target as HTMLElement | null };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.axis) {
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        // a wide table or code block that can still go the way the finger is going
        // is THEIRS: it pans, the page neither slides nor flips (the same rule the
        // sheets' body swipe keeps). A strip already at its end lets the page go.
        if (canScrollX(d.target, dx, e.currentTarget as HTMLElement)) {
          drag.current = null;
          return;
        }
        d.axis = "x";
        setDragging(true);
        try { (e.currentTarget as HTMLElement).setPointerCapture(d.pid); } catch { /* ignore */ }
      } else if (Math.abs(dy) > 8) {
        d.axis = "y";
      }
    }
    if (d.axis !== "x") return;
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.vx = (e.clientX - d.lastX) / dt;
    d.lastX = e.clientX;
    d.lastT = e.timeStamp;
    setDragX(tension(dx, W() * 0.5));
  };
  // a gesture the browser or the system takes back (a pan it owns, a sheet over
  // the page) is not a decision: the page springs home and never flips
  const onCancel = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.axis === "x") { setDragging(false); setDragX(0); }
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.axis !== "x") { setDragging(false); return; }
    const raw = d.lastX - d.x0;
    const decisive = Math.abs(raw) > W() * 0.2 || Math.abs(d.vx) > 0.35;
    setDragging(false);
    if (decisive && n > 1) onIndex(wrap(activeIdx + (raw < 0 ? 1 : -1))); // index change resets scroll+ramp
    else setDragX(0);
  };

  if (!page) return null;
  const settle = seamTransition
    ? dragging ? "--seam-fold 0.34s var(--ease-glide)" : "transform 0.42s var(--ease-soft), --seam-fold 0.34s var(--ease-glide)"
    : dragging ? "none" : "transform 0.42s var(--ease-soft)";
  return (
    <div
      ref={docRef}
      className="art-mobile-doc view-fade"
      // its own sideways swipe; the shell's section swipe must not also read it
      data-noswipe
      // double-click anywhere in the text = start editing (the sticky row
      // flips to "editing memory" + cancel + save, the viewport wears the
      // organism-coloured frame and the veil goes fully frosted).
      // Ignored while already editing — a double-click then just selects
      // the word, as in any editor.
      onDoubleClick={() => {
        if (canEdit && !isEditing) onToggleEdit?.();
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      // the seam rides the head's own spring (--seam-fold, globals.css THE
      // SEAM); an inline transition would otherwise drop it
      style={{ touchAction: "pan-y", transform: dragX ? `translateX(${dragX}px)` : undefined, transition: settle }}
    >
      <ArtifactDoc
        page={page}
        reader
        hideTitle
        highlight={highlight}
        editable={isEditing}
        overrideSource={edits?.[page.key]}
        onSaveDocument={isEditing ? (md) => onSaveDocument?.(page.key, page.projectId, md, page.editKey) : undefined}
      />
    </div>
  );
}
